/** Identidade de elementos entre revisões.
 *
 *  O problema, dito por quem o sofre. Um projectista emite um modelo.
 *  Nós penduramos coisas nos GlobalId desses elementos: linhas de
 *  custo, registos de inspecção, um tópico BCF, uma lista de defeitos.
 *  Na emissão seguinte o projectista apagou uma parede e voltou a
 *  desenhá-la, ou trocou a família, ou o exportador regenerou tudo. O
 *  GlobalId é outro e a nossa chave parte-se. A comparação diz que
 *  foram removidos cem elementos e acrescentados cem, e o relatório
 *  fica inútil precisamente quando era mais preciso.
 *
 *  O `diffContentMatch.ts` ataca isto por baixo: quando não há chave,
 *  procura quem tem o mesmo conteúdo. Este ficheiro ataca por cima, e
 *  com mais firmeza, de duas maneiras.
 *
 *  **1. Chave autoral.** Se o modelo trouxer um identificador que
 *  alguém mantém de propósito, um código de activo numa property set
 *  ou um Tag que a ferramenta preserva, esse identificador sobrevive
 *  ao redesenho. Compara-se por ele em vez do GlobalId. É a correcção
 *  a sério, e a mais barata: não há adivinhação nenhuma.
 *
 *  **2. Mapa de identidade.** Os pares em que a comparação se
 *  comprometeu podem ser gravados num ficheiro e voltar a ser lidos na
 *  próxima vez. O mapa fica preso ao SHA-256 dos dois modelos: lido
 *  contra outro par de ficheiros é RECUSADO, não aplicado às cegas. É
 *  isso que faz dele uma decisão revista e não um palpite herdado.
 *
 *  Duas regras atravessam tudo isto, e não se negoceiam:
 *
 *  - **Nada reescreve um GlobalId dentro de um ficheiro IFC.** A
 *    identidade vive à parte, num ficheiro que se pode ler, discutir e
 *    guardar ao lado dos modelos.
 *  - **Nunca se adivinha.** Uma chave que nomeia duas coisas não é uma
 *    chave: é recusada para ambas, e diz-se. O que não se consegue
 *    decidir fica por decidir.
 *
 *  Crédito: a forma deste desenho, em particular a chave autoral, o
 *  mapa preso aos digests e a recusa em vez da aplicação silenciosa,
 *  vem do que o IFClite (LTplus-AG, MPL-2.0) documenta em "Stable
 *  Element Identity". A implementação é nossa; a ideia é deles e fica
 *  dito.
 */

/** Versão do formato do mapa gravado.
 *
 *  Está em 2 e não em 1 de propósito: um mapa escrito sob chave
 *  autoral não pode ser lido por quem só saiba de GlobalId, e vale
 *  mais um leitor antigo recusar o ficheiro do que aplicá-lo sob o
 *  esquema errado. */
export const FORMATO_MAPA = 2

export type Esquema = 'globalid' | 'chave-autoral'

/** O mínimo que é preciso saber de um elemento para lhe dar chave. */
export interface ElementoComChave {
  globalId: string
  tag: string | null
  /** Propriedades no formato "Grupo.Nome", como o índice da
   *  comparação já as produz. */
  props: ReadonlyMap<string, string>
}

export interface ChaveRepetida {
  valor: string
  globalIds: string[]
}

export interface ResultadoChaves {
  /** globalId -> chave a usar na comparação. Todos os elementos
   *  aparecem aqui: quem não tem chave autoral fica com o GlobalId. */
  chaves: Map<string, string>
  comChaveAutoral: number
  semValor: number
  /** Valores que mais do que um elemento declarou. Recusados para
   *  todos os que os declararam, que voltam ao GlobalId. */
  repetidos: ChaveRepetida[]
}

/** A especificação de onde está a chave autoral.
 *
 *  Aceita-se `Tag`, que é um campo do próprio elemento, ou
 *  `Grupo.Propriedade`, que é uma property set. É o mesmo formato com
 *  que o índice da comparação já guarda as propriedades, para não
 *  haver duas gramáticas para a mesma coisa. */
export function especValida(texto: string): boolean {
  const t = texto.trim()
  if (t.length === 0) return false
  if (t.toLowerCase() === 'tag') return true
  const partes = t.split('.')
  return partes.length === 2 && partes.every((p) => p.trim().length > 0)
}

function valorDaChave(
  elemento: ElementoComChave,
  espec: string,
): string | null {
  const t = espec.trim()
  const bruto =
    t.toLowerCase() === 'tag' ? elemento.tag : (elemento.props.get(t) ?? null)
  if (bruto == null) return null
  const v = bruto.trim()
  return v.length === 0 ? null : v
}

/**
 * Decide a chave de cada elemento.
 *
 * Sem especificação, ou com uma inválida, toda a gente fica com o
 * GlobalId e não há aqui mudança nenhuma de comportamento.
 */
export function chavesPor(
  elementos: readonly ElementoComChave[],
  espec: string | null,
): ResultadoChaves {
  const chaves = new Map<string, string>()
  for (const e of elementos) chaves.set(e.globalId, e.globalId)

  if (!espec || !especValida(espec)) {
    return { chaves, comChaveAutoral: 0, semValor: elementos.length, repetidos: [] }
  }

  // Primeira passagem: quem declara o quê.
  const porValor = new Map<string, string[]>()
  let semValor = 0
  for (const e of elementos) {
    const v = valorDaChave(e, espec)
    if (v == null) {
      semValor += 1
      continue
    }
    const lista = porValor.get(v)
    if (lista) lista.push(e.globalId)
    else porValor.set(v, [e.globalId])
  }

  // Segunda passagem: só os valores que nomeiam UM elemento viram
  // chave. Um valor partilhado é recusado para todos os que o
  // declararam, e não só para o segundo a aparecer: nenhum dos dois
  // fica a saber qual deles é o legítimo, e escolher seria adivinhar.
  const repetidos: ChaveRepetida[] = []
  let comChaveAutoral = 0
  for (const [valor, globalIds] of porValor) {
    if (globalIds.length > 1) {
      repetidos.push({ valor, globalIds: [...globalIds] })
      continue
    }
    chaves.set(globalIds[0], `prop:${valor}`)
    comChaveAutoral += 1
  }

  repetidos.sort((a, b) => (a.valor < b.valor ? -1 : a.valor > b.valor ? 1 : 0))
  return { chaves, comChaveAutoral, semValor, repetidos }
}

export interface EntradaIdentidade {
  /** A chave do lado antigo. */
  antes: string
  /** A chave do lado novo. */
  depois: string
  /** Porque é que o par foi aceite. Fica escrito para poder ser
   *  discutido: "conteúdo:renomeado", "conteúdo:renomeado-e-movido". */
  razao: string
}

export interface MapaIdentidade {
  formato: number
  esquema: Esquema
  /** Só quando o esquema é chave autoral. */
  especChave?: string
  /** SHA-256 dos dois ficheiros a que este mapa diz respeito. */
  modelos: { antes: string; depois: string }
  criadoEm: string
  entradas: EntradaIdentidade[]
}

export interface ContextoDoMapa {
  antes: string
  depois: string
  esquema: Esquema
  especChave?: string
}

export function construirMapa(
  contexto: ContextoDoMapa,
  entradas: readonly EntradaIdentidade[],
  agora: Date = new Date(),
): MapaIdentidade {
  const mapa: MapaIdentidade = {
    formato: FORMATO_MAPA,
    esquema: contexto.esquema,
    modelos: {
      antes: contexto.antes.toLowerCase(),
      depois: contexto.depois.toLowerCase(),
    },
    criadoEm: agora.toISOString(),
    entradas: entradas.map((e) => ({ ...e })),
  }
  if (contexto.esquema === 'chave-autoral' && contexto.especChave) {
    mapa.especChave = contexto.especChave
  }
  return mapa
}

export type LeituraDoMapa =
  | { ok: true; pares: Map<string, string>; mapa: MapaIdentidade }
  | { ok: false; motivo: string }

function texto(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

/**
 * Lê um mapa gravado e diz se serve para esta comparação.
 *
 * Recusa em vez de aplicar quando alguma coisa não bate certo. Um mapa
 * aplicado ao par de ficheiros errado não dá erro nenhum: dá um
 * relatório limpo e falso, que é o pior resultado possível.
 */
export function lerMapa(
  bruto: unknown,
  contexto: ContextoDoMapa,
): LeituraDoMapa {
  if (!bruto || typeof bruto !== 'object') {
    return { ok: false, motivo: 'O ficheiro não é um mapa de identidade.' }
  }
  const m = bruto as Partial<MapaIdentidade>

  if (m.formato !== FORMATO_MAPA) {
    return {
      ok: false,
      motivo: `Mapa na versão ${String(m.formato ?? 'desconhecida')}, esta aplicação lê a ${FORMATO_MAPA}.`,
    }
  }
  if (m.esquema !== 'globalid' && m.esquema !== 'chave-autoral') {
    return { ok: false, motivo: 'O mapa não diz sob que esquema foi escrito.' }
  }
  if (m.esquema !== contexto.esquema) {
    return {
      ok: false,
      motivo:
        `O mapa foi escrito por ${rotulo(m.esquema)} e esta comparação ` +
        `está a correr por ${rotulo(contexto.esquema)}.`,
    }
  }
  if (
    m.esquema === 'chave-autoral' &&
    (m.especChave ?? '') !== (contexto.especChave ?? '')
  ) {
    return {
      ok: false,
      motivo:
        `O mapa foi escrito com a chave "${m.especChave ?? '(nenhuma)'}" e ` +
        `agora está a ser usada "${contexto.especChave ?? '(nenhuma)'}".`,
    }
  }

  const antes = texto(m.modelos?.antes)?.toLowerCase() ?? null
  const depois = texto(m.modelos?.depois)?.toLowerCase() ?? null
  if (!antes || !depois) {
    return { ok: false, motivo: 'O mapa não diz a que ficheiros pertence.' }
  }
  if (antes !== contexto.antes.toLowerCase()) {
    return {
      ok: false,
      motivo: 'O mapa foi escrito para outro ficheiro do lado antigo.',
    }
  }
  if (depois !== contexto.depois.toLowerCase()) {
    return {
      ok: false,
      motivo: 'O mapa foi escrito para outro ficheiro do lado novo.',
    }
  }

  if (!Array.isArray(m.entradas)) {
    return { ok: false, motivo: 'O mapa não tem entradas.' }
  }

  // A identidade é estritamente um-para-um. Uma chave que aparece duas
  // vezes de um dos lados não é identidade: é uma divisão ou uma junção,
  // e essas não cabem aqui. Vale mais recusar o ficheiro do que aplicar
  // metade dele.
  const pares = new Map<string, string>()
  const usados = new Set<string>()
  for (const e of m.entradas) {
    const a = texto((e as EntradaIdentidade)?.antes)
    const d = texto((e as EntradaIdentidade)?.depois)
    if (!a || !d) {
      return { ok: false, motivo: 'Há uma entrada sem os dois lados.' }
    }
    if (pares.has(a)) {
      return {
        ok: false,
        motivo: `A chave "${a}" aparece mais do que uma vez do lado antigo. Um mapa de identidade é um-para-um.`,
      }
    }
    if (usados.has(d)) {
      return {
        ok: false,
        motivo: `A chave "${d}" aparece mais do que uma vez do lado novo. Um mapa de identidade é um-para-um.`,
      }
    }
    pares.set(a, d)
    usados.add(d)
  }

  return { ok: true, pares, mapa: m as MapaIdentidade }
}

function rotulo(e: Esquema): string {
  return e === 'globalid' ? 'GlobalId' : 'chave autoral'
}

/** Quantas das entradas do mapa se aplicam mesmo aos dois modelos
 *  carregados. Um mapa pode estar certo e mesmo assim referir chaves
 *  que já não existem: convém dizê-lo em vez de contar como aplicadas. */
export function aplicaveis(
  pares: ReadonlyMap<string, string>,
  chavesAntes: ReadonlySet<string>,
  chavesDepois: ReadonlySet<string>,
): { aplicadas: Map<string, string>; porAplicar: number } {
  const aplicadas = new Map<string, string>()
  let porAplicar = 0
  for (const [a, d] of pares) {
    if (chavesAntes.has(a) && chavesDepois.has(d)) aplicadas.set(a, d)
    else porAplicar += 1
  }
  return { aplicadas, porAplicar }
}

/**
 * Cruza as chaves dos dois lados e diz que GlobalId antigo corresponde
 * a que GlobalId novo.
 *
 * Só as chaves autorais entram. Cruzar GlobalId com GlobalId não teria
 * efeito nenhum, porque a comparação já casa por esse.
 */
export function cruzarPorChave(
  antes: ReadonlyMap<string, string>,
  depois: ReadonlyMap<string, string>,
): Map<string, string> {
  const porChaveDepois = new Map<string, string>()
  for (const [gid, chave] of depois) {
    if (chave.startsWith('prop:')) porChaveDepois.set(chave, gid)
  }
  const fora = new Map<string, string>()
  for (const [gid, chave] of antes) {
    if (!chave.startsWith('prop:')) continue
    const novo = porChaveDepois.get(chave)
    if (novo) fora.set(gid, novo)
  }
  return fora
}

/**
 * Reescreve o índice do lado antigo para as chaves do lado novo.
 *
 * É assim que a identidade entra na comparação sem lhe mexer: depois
 * disto, o elemento redesenhado aparece dos dois lados com a mesma
 * chave e a comparação vê-o como o mesmo elemento, alterado ou não, em
 * vez de um removido mais um acrescentado. O resto do programa
 * continua a receber GlobalId verdadeiros, e por isso continua a
 * conseguir seleccionar o elemento no modelo.
 *
 * Nunca se escreve por cima de uma chave que já lá está: se isso
 * acontecesse, um elemento desaparecia do relatório em silêncio, que é
 * pior do que a churn que estamos a tentar tirar.
 */
export function reindexarPorIdentidade<T>(
  esquerda: ReadonlyMap<string, T>,
  renomear: ReadonlyMap<string, string>,
): { indice: Map<string, T>; renomeados: number; colisoes: number } {
  const indice = new Map<string, T>()
  let renomeados = 0
  let colisoes = 0
  for (const [chave, valor] of esquerda) {
    const nova = renomear.get(chave)
    if (!nova || nova === chave) {
      indice.set(chave, valor)
      continue
    }
    if (esquerda.has(nova) || indice.has(nova)) {
      colisoes += 1
      indice.set(chave, valor)
      continue
    }
    indice.set(nova, valor)
    renomeados += 1
  }
  return { indice, renomeados, colisoes }
}

/** O que a identidade fez nesta comparação, dito em texto.
 *
 *  Diz também o que NÃO conseguiu fazer. Um resumo que só conta os
 *  êxitos deixa quem lê a acreditar que o relatório está completo. */
export function resumoIdentidade(i: {
  porChave: number
  doMapa: number
  porAplicar: number
  repetidos: number
  colisoes: number
}): string {
  const partes: string[] = []
  if (i.porChave > 0) partes.push(`${i.porChave} casados pela chave`)
  if (i.doMapa > 0) partes.push(`${i.doMapa} vindos do mapa`)
  if (i.porAplicar > 0)
    partes.push(`${i.porAplicar} do mapa sem correspondência nestes modelos`)
  if (i.repetidos > 0)
    partes.push(
      `${i.repetidos} ${i.repetidos === 1 ? 'valor repetido, recusado' : 'valores repetidos, recusados'}`,
    )
  if (i.colisoes > 0)
    partes.push(`${i.colisoes} por colisão de chave`)
  return partes.length === 0 ? 'Identidade: nada a assinalar.' : `Identidade: ${partes.join(', ')}.`
}
